import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Button from "../ui/button/Button";
import axios from "../../lib/axios";
import { useAuth } from "../../context/AuthContext";
import { ThreeDots } from "react-loader-spinner";
import Loader from "../common/Loader";

export default function SetupAccountForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const authToken = searchParams.get("auth_token");
  const shop = searchParams.get("shop") || "";
  const host = searchParams.get("host") || "";

  const [credentials, setCredentials] = useState<{
    email: string;
    password: string;
    name: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Set token in localStorage if provided
    if (authToken) {
      localStorage.setItem("token", authToken);
      // Update axios default headers
      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    }

    // Fetch credentials
    const fetchCredentials = async () => {
      try {
        setLoading(true);
        const response = await axios.get("/credentials");
        setCredentials(response.data);
      } catch (err: any) {
        console.error("Failed to fetch credentials:", err);
        setError(
          err?.response?.data?.error ||
            "Failed to load credentials. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, [authToken]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!password || !passwordConfirmation) {
      setError("Please complete all required fields.");
      return;
    }

    if (password !== passwordConfirmation) {
      setError("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post("/set-password", {
        password,
        password_confirmation: passwordConfirmation,
      });

      // Update user data in localStorage immediately
      if (response.data.user && typeof window !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        console.log('Password set successfully, user data updated');
      }

      // Redirect to dashboard (auth will be refreshed on navigation)
      const basePath = shop ? "/shopify-app" : "/";
      const queryParams = new URLSearchParams();
      if (shop) queryParams.set("shop", shop);
      if (host) queryParams.set("host", host);

      navigate(`${basePath}?${queryParams.toString()}`, { replace: true });
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.response?.data?.errors?.password?.[0];
      setError(apiMessage || "Failed to set password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col flex-1">
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <div className="text-center">
            <Loader className="w-20 h-20 mx-auto mb-4" />
            {/* <p className="text-gray-600 dark:text-gray-400">Loading...</p> */}
          </div>
        </div>
      </div>
    );
  }

  if (error && !credentials) {
    return (
      <div className="flex flex-col flex-1">
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
          <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
        <div className="mb-5 sm:mb-8">
          <h1 className="mb-2 font-semibold text-gray-800 text-title-sm dark:text-white/90 sm:text-title-md">
            Welcome to Netsight!
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Your account has been created. Please save your credentials and set
            a new password to continue.
          </p>
        </div>

        {/* Credentials Display */}
        {credentials && (
          <div className="p-4 mb-6 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
            <h2 className="mb-3 text-sm font-semibold text-gray-800 dark:text-white">
              Your Account Credentials
            </h2>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-600 dark:text-gray-400">
                  Email
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="text"
                    value={credentials.email}
                    readOnly
                    className="flex-1 bg-white dark:bg-gray-800"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(credentials.email)}
                    className="shrink-0"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600 dark:text-gray-400">
                  Temporary Password
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={credentials.password}
                      readOnly
                      className="pr-10 bg-white dark:bg-gray-800"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(credentials.password)}
                    className="shrink-0"
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                </div>
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
              ⚠️ Please save these credentials. You'll need to set a new
              password below.
            </p>
          </div>
        )}

        {/* Password Reset Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
              {error}
            </div>
          )}
          <div>
            <Label>
              New Password <span className="text-error-500">*</span>
            </Label>
            <Input
              type="password"
              value={password}
              placeholder="Enter new password (min. 6 characters)"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div>
            <Label>
              Confirm Password <span className="text-error-500">*</span>
            </Label>
            <Input
              type="password"
              value={passwordConfirmation}
              placeholder="Confirm new password"
              onChange={(e) => setPasswordConfirmation(e.target.value)}
            />
          </div>
          <div>
            <Button type="submit" className="w-full" size="sm" disabled={submitting}>
              {submitting ? "Setting password..." : "Set New Password"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

